import { useState, MouseEvent } from 'react';
import {
  AppBar,
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Link,
  Badge,
  useTheme,
} from '@mui/material';
import { useApp } from '@context/AppContext';
import { useNavigate } from 'react-router-dom';
import { SettingsMenuItem } from '@constants/settingsMenuConstants';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import MobileMenu from './MobileMenu';
import DesktopMenu from './DesktopMenu';
import { LayoutConstants } from '@constants/layoutConstants';
import { clearRouteHistory } from '@services/routePersistenceService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope as faEnvelopeRegular } from '@fortawesome/free-regular-svg-icons';
import { useFetchMessageCount } from '@hooks/api/useMessageApi';
import { usePostHog } from '@posthog/react';

const NavigationMenu = () => {
  const { logout, companyInfo } = useApp();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: unreadMessageCount } = useFetchMessageCount();
  const posthog = usePostHog();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleSettingClick = (item: SettingsMenuItem) => {
    if (item.key === LayoutConstants.LOGOUT) {
      posthog?.capture('user_logged_out');
      posthog?.reset();
      logout();
      setAnchorElUser(null);
    } else if (item.path) {
      setAnchorElUser(null);
      navigate(item.path || '');
    }
  };

  const handleMenuClick = (path?: string) => {
    if (path) {
      // Clear route history when navigating to Overview to prevent persistence interference
      if (path === PrivateRoutes.ROOT) {
        clearRouteHistory();
      }
      navigate(path);
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'white',
        boxShadow: 'none',
        borderBottom: '1px solid var(--light-gray)',
        height: '80px',
        // Sits below the announcement banner when one is showing.
        top: 'var(--announcement-offset, 0px)',
        borderRadius: 0,
        width: '100%',
        left: 0,
        right: 0,
      }}
    >
      <Box
        className="header-content-container"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 6,
          mb: 2,
          pl: { xs: 2, md: 4 },
          pr: { xs: 2, md: 4 },
          width: '100%',
          height: '100%',
          margin: '0 auto',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {/* Logo Container */}
        <Box
          className="logo-container"
          sx={{ display: { xs: 'none', md: 'none', lg: 'block' } }}
        >
          {companyInfo?.contactLogoPath && (
            <Link
              href={PrivateRoutes.ROOT}
              underline="none"
              onClick={() => clearRouteHistory()}
            >
              <Box
                component="img"
                src={`${LayoutConstants.LOGO_URL}/${companyInfo.contactLogoPath}`}
                alt="Company Logo"
                sx={{
                  height: { xs: '44px', md: '70px' },
                  objectFit: 'contain',
                }}
                onError={e => {
                  const t = e.target as HTMLImageElement;
                  if (!t.src.includes('favicon')) {
                    t.src = '/favicon.svg';
                    t.style.objectFit = 'contain';
                  }
                }}
              />
            </Link>
          )}
        </Box>

        {/* Desktop Menu */}
        <DesktopMenu onMenuClick={handleMenuClick} />

        {/* Mobile Menu Button */}
        <Box
          sx={{
            display: { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MobileMenu onMenuClick={handleMenuClick} />
            {companyInfo?.contactLogoPath && (
              <Link
                href={PrivateRoutes.ROOT}
                underline="none"
                onClick={() => clearRouteHistory()}
              >
                <Box
                  component="img"
                  src={`${LayoutConstants.LOGO_URL}/${companyInfo.contactLogoPath}`}
                  alt="Company Logo"
                  sx={{
                    height: '44px',
                    objectFit: 'contain',
                  }}
                  onError={e => {
                    const t = e.target as HTMLImageElement;
                    if (!t.src.includes('favicon')) {
                      t.src = '/favicon.svg';
                      t.style.objectFit = 'contain';
                    }
                  }}
                />
              </Link>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => handleMenuClick(PrivateRoutes.MESSAGES)}
              aria-label="Messages"
              sx={{
                color: '#29323A',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Badge
                badgeContent={unreadMessageCount || 0}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '9px',
                    fontWeight: 600,
                    backgroundColor: theme.palette.teal[500],
                  },
                }}
              >
                <FontAwesomeIcon
                  icon={faEnvelopeRegular}
                  style={{ fontSize: '24px', color: '#52595F' }}
                />
              </Badge>
            </IconButton>
            <IconButton
              onClick={handleOpenUserMenu}
              aria-label="Open user menu"
              sx={{
                color: '#52595F',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <i
                className="ri-user-settings-line"
                style={{ fontSize: '24px' }}
              ></i>
            </IconButton>
          </Box>
        </Box>

        {/* Profile Menu (Desktop) */}
        <Box sx={{ display: { xs: 'none', md: 'none', lg: 'block' } }}>
          <IconButton
            onClick={handleOpenUserMenu}
            aria-label="Open user menu"
            sx={{
              color: '#29323A',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <i
              className="ri-user-settings-line"
              style={{ fontSize: '24px' }}
            ></i>
          </IconButton>
        </Box>

        {/* Profile Menu Dropdown */}
        <Menu
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableScrollLock
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              mt: 1,
            },
            // Ensure menu doesn't affect layout
            position: 'fixed',
            zIndex: 1300,
          }}
          // Prevent menu from affecting layout
          slotProps={{
            paper: {
              sx: {
                position: 'fixed',
                margin: 0,
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
              },
            },
            list: {
              sx: {
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
              },
            },
          }}
        >
          <MenuItem
            onClick={() =>
              handleSettingClick({
                key: LayoutConstants.PROFILE,
                title: LayoutConstants.PROFILE_TITLE,
                path: PrivateRoutes.PROFILE,
              })
            }
          >
            <i
              className="ri-user-line"
              style={{ marginRight: '8px', fontSize: '20px' }}
            ></i>
            <Typography
              sx={{
                fontFamily: 'Epilogue',
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: '150%',
                color: 'var(--dark-gray)',
              }}
            >
              Manage Your Profile
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSettingClick({
                key: LayoutConstants.LOGOUT,
                title: LayoutConstants.LOGOUT_TITLE,
              })
            }
          >
            <i
              className="ri-logout-box-r-line"
              style={{ marginRight: '8px', fontSize: '20px' }}
            ></i>
            <Typography
              sx={{
                fontFamily: 'Epilogue',
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: '150%',
                color: 'var(--dark-gray)',
              }}
            >
              Logout
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    </AppBar>
  );
};

export default NavigationMenu;
